import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import resolveRowsWithItems from 'owa-calendar-surface-dategrid/lib/logic/resolveRowsWithItems';
import { containsText } from 'owa-calendar-event-capabilities';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { CalendarMonthContent, SurfaceConstants } from 'owa-calendar-surface-core';
import { compareSurfaceItems } from './utils/surfaceItemsMonthSorting';
import { createMonthColumnHeader, monthDragHelperTemplate } from './Templates';
import { OwaDate, addDays, getTimestamp, getDateString } from 'owa-datetime';
import { getWeekLayout } from './logic/getWeekLayout';
import { getWeeksInMonth } from 'owa-datetime-utils';
import {
    CalendarSurfaceStore,
    createSurfaceItem,
    merge,
    SurfaceItem,
    CalendarSurfaceItemsConfig,
    CalendarItemsConfig,
    SurfaceDragDropConfig,
    getOverflowIndicatorId,
} from 'owa-calendar-surface-dategrid';
import { getDisplayMonthRangeStart } from 'owa-calendar-surface-dategrid-common-dnd';
import { getSurfaceItems } from './store/selectors/surfaceItemsSelector';
import { CalendarMonthDayCellModel } from './store/schema/CalendarMonthDayCellModel';
import getDaysInFirstWeek from './store/calculation/getDaysInFirstWeek';
import isItemBetweenDates from './store/calculation/isItemBetweenDates';
import generateMonthGrid, {
    generateWeekItems,
    createMonthRowHeaders,
} from './transformers/generateMonthGrid';

/**
 * Properties of month content generation
 */
export interface CalendarMonthTransformProps {
    monthDayCellsConfig?: CalendarItemsConfig<CalendarMonthDayCellModel>;
    renderDateHeaderAddOn?: (date: OwaDate) => JSX.Element;
    tabOrder?: string[];
    dragDropConfig?: SurfaceDragDropConfig;
    getItemConfig: (model: CalendarEvent) => CalendarSurfaceItemsConfig | null;
    shouldShowWeekMonthName?: () => boolean;
    shouldShowFullWeekDayName?: () => boolean;
}

/**
 * Creates content for mounting in calendar month view
 * @param store the calendar surface store
 * @param config the configuration for calendar month view
 * @returns structured content for month view
 */
export function getCalendarMonthContent(
    store: CalendarSurfaceStore,
    config: CalendarMonthTransformProps,
    searchText: string = undefined,
    hideTodayEmphasis: boolean = false,
    filteredItems?: CalendarEvent[]
): CalendarMonthContent {
    /** Generate day cell grid using month day cell template */
    let results = generateMonthGrid(config, store, hideTodayEmphasis);

    let items = filteredItems ?? getSurfaceItems(store);

    // slice to make shallow copy so we don't mess with the store when we set dynamic properties on items
    let sortedItems = items.sort(compareSurfaceItems).slice();
    let uniqueSortedItemKeys: string[] = [];
    let usedKeys: SurfaceItem[] = [];
    let allVisibleWeekItems: SurfaceItem[][][] = [];
    let weeksInMonth = getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);
    let shouldShowFullWeekDayName = config.shouldShowFullWeekDayName?.() ?? true;

    let maxRows =
        Math.floor(
            (store.monthHeight / weeksInMonth - SurfaceConstants.DayCellHeaderHeight) /
                SurfaceConstants.MonthRowHeight
        ) - 1;

    // If maxRows is less than 0 when monthHeight is defined (i.e. initial load is complete), then make it 0
    if (maxRows < 0 && store.monthHeight) {
        maxRows = 0;
    }

    // first header always comes first
    let from = getDisplayMonthRangeStart(store);
    let to = addDays(from, TimeConstants.DaysInOneWeek);
    let nextHeaderDate = from;
    uniqueSortedItemKeys.push('surfaceMonthHeader_' + getDateString(nextHeaderDate));
    nextHeaderDate = addDays(nextHeaderDate, 1);

    // iterate over weeks to unfold items
    for (
        let week = 0;
        week < weeksInMonth;
        week++,
            from = addDays(from, TimeConstants.DaysInOneWeek),
            to = addDays(to, TimeConstants.DaysInOneWeek)
    ) {
        const weekColumnProvider = getWeekLayout(store, from);

        // type is SurfaceItem + isUsed: boolean, use 'any' so we can set 'isUsed' boolean property on the item to prevent needing to iterate multiple times
        let weekItems: SurfaceItem[] = [];

        sortedItems
            .filter(item => isItemBetweenDates(item, from, to) && containsText(item, searchText))
            .forEach(item => {
                let intervals = weekColumnProvider.getColumnIntervals(item);

                if (intervals.length > 0) {
                    // we insert tab items for the header for every day, because we're going in order of days here
                    let start = Math.max(getTimestamp(item.Start), getTimestamp(from));
                    while (start >= getTimestamp(nextHeaderDate)) {
                        // add overflow indicator for the previous day assuming all items for previous day have been added by now
                        let prevHeaderDate = addDays(nextHeaderDate, -1);
                        uniqueSortedItemKeys.push(getOverflowIndicatorId(prevHeaderDate));
                        // now add month header for the current day before adding its items
                        uniqueSortedItemKeys.push(
                            'surfaceMonthHeader_' + getDateString(nextHeaderDate)
                        );
                        nextHeaderDate = addDays(nextHeaderDate, 1);
                    }

                    let key = item.ItemId.Id + '_' + week;

                    let surfaceItem = createSurfaceItem(merge(intervals), key, item);

                    if (!usedKeys[item.ItemId.Id]) {
                        usedKeys[item.ItemId.Id] == item;
                        uniqueSortedItemKeys.push(key);
                    }
                    weekItems.push(surfaceItem);
                }
            });

        let resolveResult = resolveRowsWithItems(
            weekItems,
            weekColumnProvider.columnCount,
            store.dayLength,
            maxRows
        );

        allVisibleWeekItems[week] = resolveResult.VisibleRows;
        results.push(
            ...generateWeekItems(store, config, resolveResult, weekColumnProvider, week, maxRows)
        );
    }

    if (store.enableDragDrop) {
        results.push(monthDragHelperTemplate(store, config, allVisibleWeekItems));
    }

    return {
        rowHeaders: createRowHeaders(store),
        columnHeaders: createMonthColumnHeaders(store, shouldShowFullWeekDayName),
        content: results,
        tabOrder: uniqueSortedItemKeys,
    };
}

function createRowHeaders(store: CalendarSurfaceStore) {
    let { dateRange, userOptions } = store;
    return userOptions.showWeekNumbers
        ? createMonthRowHeaders(
              dateRange.start,
              userOptions.weekStartDay,
              userOptions.firstWeekOfYear
          )
        : null;
}

function createMonthColumnHeaders(store: CalendarSurfaceStore, shouldShowFullWeekDayName: boolean) {
    return getDaysInFirstWeek(store).map((date, index) => {
        // in month view we want the headers to be equally spaced, so just use flex: 1 (which evenly distributes all children to space available)
        let style = {
            position: 'relative' as 'relative', // required because typescript doesn't recognize string as type 'relative', so force declaration
            flex: 1,
        };
        return createMonthColumnHeader(date, index, style, shouldShowFullWeekDayName);
    });
}
